/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles, withStyles} from '@material-ui/core';
import Step from '@material-ui/core/Step';
import StepConnector from '@material-ui/core/StepConnector';
import {StepIconProps} from '@material-ui/core/StepIcon';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import clsx from 'clsx';
import {translate} from '@ion/core/src/locale/locale';
import {
  AugmentedSmarterTrailerSensorData,
  GenericObject,
  SensorTypes,
  SmarterTrailerSensorProperties,
  TrailerDoorEvents,
  TrailerDoorStatus
} from '@ion/core/src/types/entities';
import {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import {TrailerDoorStatusIcon} from '@ion/dls/src/icons/TrailerDoorStatusIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {useSectionStyles} from './sectionStyles';

export type DoorSectionProps = {
  trailerData: AugmentedSmarterTrailerSensorData,
  sensorUpdateLoading?: boolean;
};

type DoorSensorHistoryItemProps = {
  item: GenericObject;
  index: number;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    detailTombstone : {
      height  : '228px',
      padding : '10px'
    },
    header : {
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'center',
      width          : '100%',
      fontFamily     : 'Roboto',
      fontWeight     : 600,
      fontSize       : '14px',
      lineHeight     : '17px',
      color          : dlsColor('black')
    },
    batteryLevel : {
      display    : 'flex',
      alignItems : 'center',
      ...dlsTypography('slider')
    },
    batteryLevelLabel : {
      marginRight : 5,
      color       : dlsColor('grey2'),
      fontWeight  : 'bold'
    },
    batteryLevelNormal : {
      color : dlsColor('grey3')
    },
    batteryLevelLow : {
      color : 'red'
    },
    content : {
      padding : '15px'
    },
    historyContent : {
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'flex-start'
    },
    historyListContainer : {
      padding : '0 0 0 5px'
    },
    historyIconRoot : {
      color : dlsColor('grey3')
    },
    historyIconLatest : {
      color : dlsColor('caBlue')
    },
    historyIconCircle : {
      width           : 8,
      height          : 8,
      borderRadius    : '50%',
      backgroundColor : 'currentColor'
    },
    historyItem : {
      fontSize      : '12px',
      fontWeight    : 'bold',
      letterSpacing : '1px',
      lineHeight    : '18px',
      textTransform : 'uppercase'
    },
    historyItemNow : {
      color : dlsColor('caBlue')
    },
    historyItemEvent : {
      color : dlsColor('grey1')
    },
    historyItemTime : {
      color : dlsColor('grey3')
    },
    historyDiagram : {
      display        : 'flex',
      alignSelf      : 'center',
      justifyContent : 'center'
    },
    ...getAccordionWrapperStyles()
  };
});

const DoorSensorStepIcon = (props: StepIconProps) => {
  const classes = useStyles();
  const {active} = props;
  return (
    <div className={clsx(
      classes.historyIconRoot, {
        [classes.historyIconLatest] : active
      })}>
      <div className={classes.historyIconCircle} />
    </div>
  );
};

const DoorSensorStepConnector = withStyles(theme => ({
  vertical : {
    marginLeft : 3,
    padding    : 0
  },
  lineVertical : {
    minHeight : 15
  }
}))(StepConnector);

const DoorSensorHistoryItem = ({
  item,
  index
}: DoorSensorHistoryItemProps) => {
  const classes        = useStyles();
  const sectionClasses = useSectionStyles();
  const {isCurrent}    = useThemeContext();
  const {
    messageTime,
    doorState
  } = item;
  const event = translate(
    `common:smarterTrailer.door.events.${
      doorState === TrailerDoorStatus.Open
        ? TrailerDoorEvents.Opened
        : TrailerDoorEvents.Closed
    }`);

  return (
    <div className={themedClassName(classes.historyItem, sectionClasses.currentLabel, isCurrent)}>
      {index === 0 &&
        <div className={classes.historyItemNow}>
          {translate('common:smarterTrailer.door.now')}
        </div>
      }
      <div className={classes.historyItemEvent}>
        {`${translate('common:smarterTrailer.door.label')} ${event}`}
      </div>
      <div className={classes.historyItemTime}>
        {getFormattedDate(messageTime)}
      </div>
    </div>
  );
};

/**
 * Door Sensor section of the smarter trailer content that contains the
 * recent door sensor history content and trailer door icon.
 *
 * ```typescript
 *  import {DoorSection} from '@ion/features/src/smarterTrailer';
 *
 *  <DoorSection
 *    trailerData={trailerData}
 *    sensorUpdateLoading={sensorUpdateLoading} />
 * ```
 */
export const DoorSensorSection = ({
  trailerData,
  sensorUpdateLoading
}: DoorSectionProps) => {
  const classes         = useStyles();
  const sectionClasses  = useSectionStyles();
  const {isCurrent}     = useThemeContext();
  const history         = trailerData?.[SensorTypes.DoorState]?.doorHistory?.slice(0, 4);
  const {batteryLevel}  = history?.[0];

  if (!sensorUpdateLoading) {
    return (
      <div className={themedClassName(classes.content, sectionClasses.currentContent, isCurrent)}>
        <div className={classes.historyContent}>
          <div>
            <Stepper orientation='vertical'
              connector={<DoorSensorStepConnector />}
              activeStep={0}
              classes={{
                root : themedClassName(
                  classes.historyListContainer,
                  sectionClasses.currentContent,
                  isCurrent
                )
              }}>
              {history?.map((item, index) => (
                <Step key={index}>
                  <StepLabel StepIconComponent={DoorSensorStepIcon}>
                    <DoorSensorHistoryItem item={item}
                      index={index} />
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>
          <div className={classes.historyDiagram}>
            <TrailerDoorStatusIcon status={history?.[0]?.[SmarterTrailerSensorProperties.DoorState]} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={classes.detailTombstone}>
      <TombstoneLoader />
    </div>
  );
};

export default DoorSensorSection;
